// src/scenes/dashboardstudent/StudentDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    CircularProgress,
    Button,
    Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveBar } from "@nivo/bar";

const StudentDashboard = () => {
    const theme = useTheme();
    const textColor = theme.palette.text.primary;
    const red = theme.palette.error.main;     // Free
    const green = theme.palette.success.main; // Paid
    const blue = theme.palette.info.main;     // Reduced

    const [allStudents, setAllStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [site, setSite] = useState("All Sites");

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const stuRes = await fetch(
                    "https://figazuresql.swancreate.com/api/swa/Students"
                );
                if (!stuRes.ok) throw new Error(`Students HTTP ${stuRes.status}`);
                const students = await stuRes.json();
                // normalize status to single-letter code:
                const norm = students.map((s) => ({
                    ...s,
                    PaymentStatus:
                        /^F/i.test(s.PaymentStatus) ? "F" :
                            /^R/i.test(s.PaymentStatus) ? "R" :
                                "P", // default to Paid
                }));
                setAllStudents(norm);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const sites = useMemo(() => {
        const names = Array.from(
            new Set(allStudents.map((s) => s.SchoolName).filter(Boolean))
        ).sort();
        return ["All Sites", ...names];
    }, [allStudents]);

    const filtered = useMemo(
        () =>
            site !== "All Sites"
                ? allStudents.filter((s) => s.SchoolName === site)
                : allStudents,
        [site, allStudents]
    );

    const totals = useMemo(() => {
        const agg = { Free: 0, Paid: 0, Reduced: 0, NegBal: 0, PosBal: 0 };
        filtered.forEach((s) => {
            if (s.PaymentStatus === "F") agg.Free++;
            else if (s.PaymentStatus === "R") agg.Reduced++;
            else agg.Paid++;
            const bal = Number(s.Account_Balance) || 0;
            if (bal < 0) agg.NegBal += bal;
            else agg.PosBal += bal;
        });
        const total = filtered.length || 1;
        return {
            ...agg,
            FreePct: ((agg.Free / total) * 100).toFixed(1),
            PaidPct: ((agg.Paid / total) * 100).toFixed(1),
            RedPct: ((agg.Reduced / total) * 100).toFixed(1),
            TotalStudents: filtered.length,
        };
    }, [filtered]);

    if (loading)
        return (
            <Box m={2} display="flex" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    if (error)
        return (
            <Box m={2}>
                <Typography color={red}>Error: {error}</Typography>
            </Box>
        );

    const pieData = [
        { id: "Free", label: "Free", value: totals.Free, color: red },
        { id: "Paid", label: "Paid", value: totals.Paid, color: green },
        { id: "Reduced", label: "Reduced", value: totals.Reduced, color: blue },
    ];

    // bar: one entry per site (exclude "All Sites")
    const barData = sites
        .filter((s) => s !== "All Sites")
        .map((s) => {
            const grp = allStudents.filter((st) => st.SchoolName === s);
            const f = grp.filter((st) => st.PaymentStatus === "F").length;
            const r = grp.filter((st) => st.PaymentStatus === "R").length;
            const p = grp.filter((st) => st.PaymentStatus === "P").length;
            return { site: s, Free: f, Paid: p, Reduced: r };
        });

    const columns = [
        { field: "FirstName", headerName: "First Name", flex: 1 },
        { field: "LastName", headerName: "Last Name", flex: 1 },
        { field: "Grade", headerName: "Grade", flex: 0.5 },
        { field: "StudentEmail", headerName: "Email", flex: 1.5 },
        {
            field: "Account_Balance",
            headerName: "Balance",
            flex: 1,
            valueFormatter: ({ value }) => `$${Number(value).toFixed(2)}`,
        },
        {
            field: "PaymentStatus",
            headerName: "Status",
            flex: 0.7,
            valueFormatter: ({ value }) =>
                value === "F" ? "Free" : value === "R" ? "Reduced" : "Paid",
        },
    ];

    const downloadCSV = () => {
        const header = ["First", "Last", "Grade", "Email", "Balance", "Status"];
        const rows = filtered.map((r) => [
            r.FirstName,
            r.LastName,
            r.Grade,
            r.StudentEmail,
            r.Account_Balance,
            r.PaymentStatus === "F"
                ? "Free"
                : r.PaymentStatus === "R"
                    ? "Reduced"
                    : "Paid",
        ]);
        const csv = [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "students.csv";
        a.click();
    };

    return (
        <Box m="20px">
            <Typography variant="h4" fontWeight="bold" mb="20px" color={textColor}>
                Student Dashboard
            </Typography>

            {/* Site Selector */}
            <FormControl sx={{ mb: 3, minWidth: 240 }}>
                <InputLabel sx={{ color: textColor }}>Site</InputLabel>
                <Select
                    value={site}
                    onChange={(e) => setSite(e.target.value)}
                    sx={{
                        color: textColor,
                        ".MuiOutlinedInput-notchedOutline": { borderColor: textColor },
                    }}
                >
                    {sites.map((s) => (
                        <MenuItem key={s} value={s}>
                            {s}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Stat Boxes */}
            <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fit,minmax(160px,1fr))"
                gap="20px"
                mb="40px"
            >
                {[
                    {
                        label: "Total Students",
                        value: totals.TotalStudents,
                        color: theme.palette.primary.main,
                    },
                    {
                        label: "Free",
                        value: `${totals.Free} (${totals.FreePct}%)`,
                        color: red,
                    },
                    {
                        label: "Paid",
                        value: `${totals.Paid} (${totals.PaidPct}%)`,
                        color: green,
                    },
                    {
                        label: "Reduced",
                        value: `${totals.Reduced} (${totals.RedPct}%)`,
                        color: blue,
                    },
                    {
                        label: "� Balance",
                        value: `$${totals.NegBal.toFixed(2)}`,
                        color: red,
                    },
                    {
                        label: "+ Balance",
                        value: `$${totals.PosBal.toFixed(2)}`,
                        color: green,
                    },
                ].map(({ label, value, color }) => (
                    <Box
                        key={label}
                        sx={{
                            p: 2,
                            textAlign: "center",
                            border: `2px solid ${color}`,
                            borderRadius: 2,
                            boxShadow: `0 0 12px ${color}66`,
                            backgroundColor: theme.palette.background.paper,
                        }}
                    >
                        <Typography variant="h5" color={textColor}>
                            {value}
                        </Typography>
                        <Typography variant="subtitle2" color={color}>
                            {label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Pie Chart */}
            <Box height={300} mb={4}>
                <ResponsivePie
                    data={pieData}
                    margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                    colors={(d) => d.data.color}
                    theme={{
                        textColor,
                        tooltip: {
                            container: { background: theme.palette.background.paper },
                        },
                    }}
                    innerRadius={0.5}
                    padAngle={0.7}
                    cornerRadius={3}
                    borderWidth={1}
                    borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                    arcLinkLabelsTextColor={textColor}
                    arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
                />
            </Box>

            {/* Bar Chart */}
            <Box component={Paper} p={2} mb={4} height={400}>
                <Typography mb={2} color={textColor}>
                    Free/Paid/Reduced by Site (click to filter)
                </Typography>
                <ResponsiveBar
                    data={barData}
                    keys={["Free", "Paid", "Reduced"]}
                    indexBy="site"
                    margin={{ top: 20, right: 20, bottom: 150, left: 60 }}
                    padding={0.3}
                    colors={[red, green, blue]}
                    theme={{
                        axis: {
                            ticks: { text: { fill: textColor } },
                            legend: { text: { fill: textColor } },
                        },
                    }}
                    axisBottom={{ tickRotation: -45 }}
                    enableLabel={false}
                    onClick={(bar) => {
                        const clicked = bar.data.site;
                        setSite((prev) => (prev === clicked ? "All Sites" : clicked));
                    }}
                />
            </Box>

            {/* Download CSV */}
            <Button
                variant="contained"
                sx={{
                    mb: 2,
                    backgroundColor: green,
                    "&:hover": { backgroundColor: green },
                }}
                onClick={downloadCSV}
            >
                Download CSV
            </Button>

            {/* Student Table */}
            <Box height={500}>
                <DataGrid
                    rows={filtered.map((r, i) => ({
                        id: i,
                        FirstName: r.FirstName,
                        LastName: r.LastName,
                        Grade: r.Grade,
                        StudentEmail: r.StudentEmail,
                        Account_Balance: r.Account_Balance,
                        PaymentStatus: r.PaymentStatus,
                    }))}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    sx={{
                        ".MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor: theme.palette.action.hover,
                        },
                        ".MuiDataGrid-row:nth-of-type(odd)": {
                            backgroundColor: theme.palette.background.default,
                        },
                        ".MuiDataGrid-columnHeaders": {
                            backgroundColor: theme.palette.background.paper,
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

export default StudentDashboard;
